import { ConfirmDialog } from '@tix-ticketing/ui-components';
import {
  TicketsAlertIcon,
  TicketsRemoveIcon,
} from '@tix-ticketing/ui-icons/feature';
import { FormattedMessage, useIntl } from 'react-intl';

type ItemType =
  'tickets' | 'giftCard' | 'membership' | 'donation' | 'subscription';

export function DeleteConfirmationDialog({
  open,
  onClose,
  onDelete,
  loading,
  itemType = 'tickets',
  releaseFrom,
}: {
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
  loading?: boolean;
  itemType?: ItemType;
  /**
   * Set when the item is taken off a reservation or a subscription renewal
   * opened from My pages. The copy then names the order it comes off, and
   * speaks of releasing whatever the venue goes on holding until checkout.
   */
  releaseFrom?: 'reservation' | 'renewal';
}) {
  const intl = useIntl();

  const removeText: Record<ItemType, { title: string; description: string }> = {
    tickets: {
      title: intl.formatMessage({
        id: 'cart.removeTickets.title',
        defaultMessage: 'Remove tickets?',
      }),
      description: intl.formatMessage({
        id: 'cart.removeTickets.description',
        defaultMessage:
          'Do you want to remove the tickets from your order and start over?',
      }),
    },
    subscription: {
      title: intl.formatMessage({
        id: 'cart.removeSubscription.title',
        defaultMessage: 'Remove subscription',
      }),
      description: intl.formatMessage({
        id: 'cart.removeSubscription.description',
        defaultMessage:
          'Do you want to remove the subscription from your cart?',
      }),
    },
    giftCard: {
      title: intl.formatMessage({
        id: 'cart.removeGiftCard.title',
        defaultMessage: 'Remove gift card?',
      }),
      description: intl.formatMessage({
        id: 'cart.removeGiftCard.description',
        defaultMessage: 'Do you want to remove the gift card from your order?',
      }),
    },
    membership: {
      title: intl.formatMessage({
        id: 'cart.removeMembership.title',
        defaultMessage: 'Remove membership?',
      }),
      description: intl.formatMessage({
        id: 'cart.removeMembership.description',
        defaultMessage: 'Do you want to remove the membership from your order?',
      }),
    },
    donation: {
      title: intl.formatMessage({
        id: 'cart.removeDonation.title',
        defaultMessage: 'Remove donation?',
      }),
      description: intl.formatMessage({
        id: 'cart.removeDonation.description',
        defaultMessage: 'Do you want to remove the donation from your order?',
      }),
    },
  };

  const releaseFromReservationText: Record<
    ItemType,
    { title: string; description: string }
  > = {
    tickets: {
      title: intl.formatMessage({
        id: 'cart.releaseFromReservation.tickets.title',
        defaultMessage: 'Release tickets?',
      }),
      description: intl.formatMessage({
        id: 'cart.releaseFromReservation.tickets.description',
        defaultMessage:
          'Do you want to release the tickets from your reservation?',
      }),
    },
    subscription: {
      title: intl.formatMessage({
        id: 'cart.releaseFromReservation.subscription.title',
        defaultMessage: 'Release subscription?',
      }),
      description: intl.formatMessage({
        id: 'cart.releaseFromReservation.subscription.description',
        defaultMessage:
          'Do you want to release the subscription from your reservation?',
      }),
    },
    giftCard: {
      title: intl.formatMessage({
        id: 'cart.releaseFromReservation.giftCard.title',
        defaultMessage: 'Release gift card?',
      }),
      description: intl.formatMessage({
        id: 'cart.releaseFromReservation.giftCard.description',
        defaultMessage:
          'Do you want to release the gift card from your reservation?',
      }),
    },
    membership: {
      title: intl.formatMessage({
        id: 'cart.removeFromReservation.membership.title',
        defaultMessage: 'Remove membership?',
      }),
      description: intl.formatMessage({
        id: 'cart.removeFromReservation.membership.description',
        defaultMessage:
          'Do you want to remove the membership from your reservation?',
      }),
    },
    donation: {
      title: intl.formatMessage({
        id: 'cart.releaseFromReservation.donation.title',
        defaultMessage: 'Release donation?',
      }),
      description: intl.formatMessage({
        id: 'cart.releaseFromReservation.donation.description',
        defaultMessage:
          'Do you want to release the donation from your reservation?',
      }),
    },
  };

  const releaseSeatsFromRenewalText = {
    title: intl.formatMessage({
      id: 'cart.releaseFromRenewal.title',
      defaultMessage: 'Release seats?',
    }),
    description: intl.formatMessage({
      id: 'cart.releaseFromRenewal.description',
      defaultMessage:
        'Do you want to release these seats from your subscription renewal?',
    }),
  };

  const releaseFromRenewalText: Record<
    ItemType,
    { title: string; description: string }
  > = {
    tickets: releaseSeatsFromRenewalText,
    subscription: releaseSeatsFromRenewalText,
    giftCard: {
      title: intl.formatMessage({
        id: 'cart.releaseFromRenewal.giftCard.title',
        defaultMessage: 'Release gift card?',
      }),
      description: intl.formatMessage({
        id: 'cart.releaseFromRenewal.giftCard.description',
        defaultMessage:
          'Do you want to release the gift card from your subscription renewal?',
      }),
    },
    membership: {
      title: intl.formatMessage({
        id: 'cart.removeFromRenewal.membership.title',
        defaultMessage: 'Remove membership?',
      }),
      description: intl.formatMessage({
        id: 'cart.removeFromRenewal.membership.description',
        defaultMessage:
          'Do you want to remove the membership from your subscription renewal?',
      }),
    },
    donation: removeText.donation,
  };

  const text =
    releaseFrom === 'renewal'
      ? releaseFromRenewalText[itemType]
      : releaseFrom === 'reservation'
        ? releaseFromReservationText[itemType]
        : removeText[itemType];

  // A membership or a donation is deleted outright rather than released.
  const isRelease =
    !!releaseFrom && itemType !== 'membership' && itemType !== 'donation';

  return (
    <ConfirmDialog
      open={open}
      icon={
        itemType === 'subscription' ? (
          <TicketsAlertIcon />
        ) : (
          <TicketsRemoveIcon />
        )
      }
      title={text.title}
      description={text.description}
      confirmLabel={
        isRelease ? (
          <FormattedMessage id="common.release" defaultMessage="Release" />
        ) : (
          <FormattedMessage id="common.remove" defaultMessage="Remove" />
        )
      }
      cancelLabel={
        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
      }
      pending={loading}
      onConfirm={onDelete}
      onClose={onClose}
    />
  );
}
